/**
 * Ключ поиска по меню: латиница и кириллица сводятся к одной записи.
 *
 * Названия блюд приходят с бэкенда на языке интерфейса — при русском меню они
 * кириллицей. Клиент же набирает как удобно: «lavash» с латинской раскладки,
 * «пицца» вместо «pizza», узбекское «lagʻmon» вместо «лагмон». Обычный
 * `includes` в таких парах не находит ничего, поэтому и запрос, и название
 * приводим к одному ключу и сравниваем уже его.
 *
 * Ключ намеренно грубый: он сводит вместе то, что звучит одинаково
 * (`ts` и `ц`, `c`, `k` и `q`, `x` и `х`), и теряет разницу, которой в поиске
 * по меню всё равно нет. Ложные совпадения тут дешевле пустого экрана.
 */

/** Кириллица в латиницу — многобуквенные соответствия дальше сведёт `fold`. */
const CYRILLIC: Record<string, string> = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'yo', ж: 'zh', з: 'z',
  и: 'i', й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r',
  с: 's', т: 't', у: 'u', ф: 'f', х: 'h', ц: 'ts', ч: 'ch', ш: 'sh', щ: 'sch',
  ъ: '', ы: 'i', ь: '', э: 'e', ю: 'yu', я: 'ya',
  /* Узбекская кириллица */
  ў: 'o', қ: 'q', ғ: 'g', ҳ: 'h', ң: 'n',
}

/** Апострофы узбекской латиницы: `oʻ`, `gʼ` — для поиска это те же `o` и `g`. */
const APOSTROPHES = /[ʻʼ'`´ʹ]/g

/** Диакритика: `döner` и `doner` для клиента одно и то же слово. */
function flatten(value: string): string {
  return value.normalize('NFD').replace(/[̀-ͯ]/g, '')
}

/**
 * Сведение латиницы: сначала диграфы (их дают и транслитерация, и сам клиент),
 * затем буквы, которые в разных раскладках пишут по-разному. `ş` и `ç` —
 * служебные символы для «ш» и «ч», наружу ключ всё равно не показываем.
 */
function fold(value: string): string {
  return value
    .replace(APOSTROPHES, '')
    .replace(/shch|sch|sh/g, 'ş')
    .replace(/ch/g, 'ç')
    .replace(/kh|zh/g, 'h')
    /* `mojito` и «мохито», `julien` и «жульен»: ж, х и j в меню не спорят */
    .replace(/j/g, 'h')
    /* `pizza` и «пицца», `tsezar` и «цезарь» */
    .replace(/ts|zz/g, 'c')
    .replace(/yo/g, 'e')
    .replace(/x/g, 'h')
    /* `cola` и «кола», `qovurma` и «ковурма» */
    .replace(/[cqk]/g, 'k')
    .replace(/w/g, 'v')
    .replace(/y/g, 'i')
    /* Сдвоенные буквы: «лаваш» пишут и с одной `l`, и с двумя */
    .replace(/(.)\1+/g, '$1')
    .replace(/[^a-z0-9şç]/g, '')
}

export function searchKey(value: string): string {
  const lower = flatten(value.toLowerCase())

  let latin = ''
  for (const char of lower) latin += CYRILLIC[char] ?? char

  return fold(latin)
}
